import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, PieChart, History, Users, LineChart, Clock12 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";

// Dropdown component for Home menu (LEFT side of screen)
const HomeDropdown = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className="fixed bottom-16 left-4 w-56 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-background shadow-lg z-50 overflow-hidden">
      <Link
        to="/dashboard"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">HOME</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">DASHBOARD OVERVIEW</div>
      </Link>
      <Link
        to="/reward"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">REWARD</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">CLAIM YOUR REWARDS & BOUNTIES</div>
      </Link>
    </div>
  );
};

// Dropdown component for Trade menu (RIGHT side of screen)
const TradeDropdown = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className="fixed bottom-16 right-4 w-56 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-background shadow-lg z-50 overflow-hidden">
      <Link
        to="/convert"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">MARKET TRADE</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">QUICK MARKET ORDERS</div>
      </Link>
      <Link
        to="/swap"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">DEX SWAP</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">DECENTRALIZED EXCHANGE SWAP</div>
      </Link>
      <Link
        to="/limit-order"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">LIMIT ORDER</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">SET PRICE TARGETS</div>
      </Link>
    </div>
  );
};

// Dropdown component for Assets menu (CENTER of screen)
const AssetsDropdown = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className="fixed bottom-16 left-1/2 transform -translate-x-1/2 w-56 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-background shadow-lg z-50 overflow-hidden">
      <Link
        to="/assets"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">MY PORTFOLIO</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">VIEW ALL YOUR ASSETS</div>
      </Link>
      <Link
        to="/withdraw"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">WITHDRAW</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">SEND FUNDS TO EXTERNAL WALLET</div>
      </Link>
      <Link
        to="/transfer"
        className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800"
        onClick={onClose}
      >
        <div className="font-medium text-xs text-gray-900 dark:text-white uppercase">TRANSFER</div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase">TRANSFER BETWEEN WALLETS</div>
      </Link>
    </div>
  );
};

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // 🔐 SECURE USER LOADING using HttpOnly cookie
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setCurrentUser(data.user);
            setLoading(false);
            return;
          }
        }
        setCurrentUser(null);
      } catch (error) {
        console.error("[MobileNav] Auth error:", error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleDropdownToggle = (dropdownName: string) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownName);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <nav className={cn(
      "fixed bottom-0 left-0 right-0 md:hidden border-t z-40 pb-1.5 shadow-sm",
      "bg-white dark:bg-background border-gray-200 dark:border-gray-800"
    )}>
      <div className="w-full flex justify-around items-center h-16 px-2">
        
        {/* HOME Button */}
        <button
          ref={(el) => { buttonRefs.current.home = el; }}
          onClick={() => handleDropdownToggle("home")}
          className={cn(
            "flex flex-col items-center justify-center px-1.5 py-1 text-[11px] font-bold transition-colors flex-1 uppercase",
            openDropdown === "home" || location.pathname === "/dashboard" || location.pathname === "/reward"
              ? "text-teal-500 dark:text-teal-400"
              : "text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white"
          )}
        >
          <Home className="w-5 h-5 mb-1" />
          HOME
        </button>
        <HomeDropdown isOpen={openDropdown === "home"} onClose={() => setOpenDropdown(null)} />

        {/* TRADE Button */}
        <button
          ref={(el) => { buttonRefs.current.trade = el; }}
          onClick={() => handleDropdownToggle("trade")}
          className={cn(
            "flex flex-col items-center justify-center px-1.5 py-1 text-[11px] font-bold transition-colors flex-1 uppercase",
            openDropdown === "trade" || location.pathname === "/convert" || location.pathname === "/swap" || location.pathname === "/limit-order"
              ? "text-teal-500 dark:text-teal-400"
              : "text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white"
          )}
        >
          <PieChart className="w-5 h-5 mb-1" />
          TRADE
        </button>
        <TradeDropdown isOpen={openDropdown === "trade"} onClose={() => setOpenDropdown(null)} />

        {/* ASSETS Button */}
        <button
          ref={(el) => { buttonRefs.current.assets = el; }}
          onClick={() => handleDropdownToggle("assets")}
          className={cn(
            "flex flex-col items-center justify-center px-1.5 py-1 text-[11px] font-bold transition-colors flex-1 uppercase",
            openDropdown === "assets" || location.pathname === "/assets" || location.pathname === "/withdraw" || location.pathname === "/transfer"
              ? "text-teal-500 dark:text-teal-400"
              : "text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white"
          )}
        >
          <LineChart className="w-5 h-5 mb-1" />
          ASSET
        </button>
        <AssetsDropdown isOpen={openDropdown === "assets"} onClose={() => setOpenDropdown(null)} />

        {/* P2P Button - Direct Link */}
        <Link
          to="/p2p"
          className={cn(
            "flex flex-col items-center justify-center px-1.5 py-1 text-[11px] font-bold transition-colors flex-1 uppercase",
            location.pathname === "/p2p"
              ? "text-teal-500 dark:text-teal-400"
              : "text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white"
          )}
        >
          <Users className="w-5 h-5 mb-1" />
          EXPRESS
        </Link>

        {/* History Button - Direct Link */}
        <Link
          to="/history"
          className={cn(
            "flex flex-col items-center justify-center px-1.5 py-1 text-[11px] font-bold transition-colors flex-1 uppercase",
            location.pathname === "/history"
              ? "text-teal-500 dark:text-teal-400"
              : "text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white"
          )}
        >
          <Clock12 className="w-5 h-5 mb-1" />
          HISTORY
        </Link>

      </div>
    </nav>
  );
}
